/**
 * KAIDEV-3592 — closeout flow color + radius tokens.
 *
 * Thin local seam re-exporting the 2026 Kai Styleguide palette (from the
 * kai-shiki core token module) so the closeout
 * flow's per-instance styles read from
 * one named source instead of scattered hardcoded hexes. The styleguide is the
 * design system home; this module just gives the closeout flow a single import.
 *
 * NOTE: per-task group *color identities* (the data-driven accent dots/stripes
 * from figmaColorScales via the adapter) are NOT theme tokens — those stay as
 * the adapter emits them. Only the chrome/surface/text colors map here.
 */
import { styleguideColors as styleguideColorsExtended } from '@/Client/Styles/kaiShikiTokens';
import { styleguideRadius } from '@kaiizen/kai-shiki/core';

const c = styleguideColorsExtended;
const r = styleguideRadius;

/** Closeout-flow palette (maps the styleguide tokens to closeout-friendly names). */
export const COLORS = {
  ink: c.ink,
  white: c.white,
  paper: c.paper,
  surfaceMuted: c.surfaceMuted,
  stroke: c.stroke,
  strokeFaded: c.strokeFaded,
  midtone: c.midtone,
  midtoneFaded: c.midtoneFaded,
  /** mockup --t2 (#5C5750) — secondary body text, control labels. */
  textSecondary: c.textSecondary,
  /** mockup --t3 (#8A8470) — captions, muted metadata. */
  textTertiary: c.textTertiary,
  accent: c.accent,
  success: c.success,
  error: c.error,
  /** mockup --sunken (#F4F0E6) maps directly to kai-shiki styleguide sunken. */
  sunken: c.sunken,
  /** mockup --app-bg (#FFFDF8); kai-shiki surfaceMuted (#FAF8F2) is the closest token. */
  appBg: c.surfaceMuted,
  /** mockup --wash (#FDFDEA) — hover tone for selectable rows. */
  wash: c.wash,
  /** mockup --sel-wash (#FDFDD6) — selected tone for selectable rows. */
  selectedWash: c.selectedWash,
} as const;

/** Radii: sharp everywhere except status dots / avatars (circle). */
export const RADIUS = {
  sharp: r.sharp,
  circle: r.circle,
} as const;

/** Font families (2026 Kai Styleguide). */
export const FONT = {
  /** Page titles, section headings, document titles. */
  display: 'Maxeville, "Helvetica Neue", sans-serif',
  /** Body copy, item descriptions, numeric amounts. */
  body: 'CircularXX, -apple-system, BlinkMacSystemFont, "Avenir Next", "Segoe UI", Roboto, sans-serif',
  /** Labels, page tabs, buttons, status pills, fieldset legends. */
  mono: '"Maxeville Construct", Maxeville, monospace',
} as const;

/** Translucent overlay helper (borders/backgrounds at partial opacity). */
export const withAlpha = (hex: string, alpha: number): string => {
  const n = parseInt(hex.slice(1), 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
};
